import * as THREE from 'three'
import { COURT_WIDTH, COURT_HEIGHT } from '../court/layout.js'

/**
 * Three.js scene setup: renderer, camera, lawn, lighting.
 *
 * Coordinate system:
 *   - Three.js uses right-handed coords with Y up
 *   - We use a top-down view, so our 2D physics (x, y) maps to Three.js (x, -z)
 *   - Physics y=0 is top of court, y=COURT_HEIGHT is bottom
 *   - Helper: physicsToThree(px, py) → THREE.Vector3
 */

export const PIXELS_PER_METRE = 60  // display scale

export function physicsToThree(px: number, py: number, elevation = 0): THREE.Vector3 {
  // Physics coords: x right, y down
  // Three.js coords: x right, y up, z toward viewer
  // Top-down camera looks down -y, so physics y maps to -z in world space
  return new THREE.Vector3(px - COURT_WIDTH / 2, elevation, py - COURT_HEIGHT / 2)
}

export class GameScene {
  scene: THREE.Scene
  camera: THREE.OrthographicCamera
  renderer: THREE.WebGLRenderer

  // Lighting refs for evening mode
  ambientLight: THREE.AmbientLight
  eveningSpot1: THREE.SpotLight
  eveningSpot2: THREE.SpotLight

  constructor(canvas: HTMLCanvasElement) {
    // ── Renderer ──────────────────────────────────────────
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap

    // ── Scene ──────────────────────────────────────────────
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0x87ceeb)  // daytime sky blue

    // ── Camera ─────────────────────────────────────────────
    // Orthographic top-down. Must set camera.up to avoid gimbal lock
    // when looking straight down along -Y axis.
    this.camera = new THREE.OrthographicCamera(0, 0, 0, 0, 0.1, 200)
    this.camera.position.set(0, 50, 0)
    this.camera.up.set(0, 0, -1)   // "up" on screen = -Z in world
    this.camera.lookAt(0, 0, 0)
    this.scene.add(this.camera)

    // ── Lighting ───────────────────────────────────────────
    this.ambientLight = new THREE.AmbientLight(0xffffff, 1.0)
    this.scene.add(this.ambientLight)

    const dirLight = new THREE.DirectionalLight(0xffffff, 0.6)
    dirLight.position.set(5, 15, 5)
    dirLight.castShadow = true
    this.scene.add(dirLight)

    // Evening floodlights (off by default, activated from hoop 3)
    this.eveningSpot1 = new THREE.SpotLight(0xffffa0, 0, 50, Math.PI / 4)
    this.eveningSpot1.position.set(-COURT_WIDTH * 0.4, 15, -COURT_HEIGHT * 0.4)
    this.scene.add(this.eveningSpot1)
    this.eveningSpot1.target.position.set(0, 0, 0)
    this.scene.add(this.eveningSpot1.target)

    this.eveningSpot2 = new THREE.SpotLight(0xffffa0, 0, 50, Math.PI / 4)
    this.eveningSpot2.position.set(COURT_WIDTH * 0.4, 15, COURT_HEIGHT * 0.4)
    this.scene.add(this.eveningSpot2)
    this.eveningSpot2.target.position.set(0, 0, 0)
    this.scene.add(this.eveningSpot2.target)

    this._buildLawn()
    this.handleResize()
    window.addEventListener('resize', () => this.handleResize())
  }

  private _buildLawn(): void {
    // Alternating mown stripes (two shades of green)
    const stripeCount = 10
    const stripeWidth = COURT_HEIGHT / stripeCount
    const colors = [0x2d8a2d, 0x329632]

    for (let i = 0; i < stripeCount; i++) {
      const geo = new THREE.PlaneGeometry(COURT_WIDTH, stripeWidth)
      const mat = new THREE.MeshLambertMaterial({ color: colors[i % 2] })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.rotation.x = -Math.PI / 2
      mesh.position.set(
        0,
        -0.001,  // just below y=0 to avoid z-fighting
        (i * stripeWidth) - COURT_HEIGHT / 2 + stripeWidth / 2
      )
      mesh.receiveShadow = true
      this.scene.add(mesh)
    }

    // Court boundary lines
    const lineColor = 0xffffff
    const lineMat = new THREE.LineBasicMaterial({ color: lineColor, transparent: true, opacity: 0.4 })
    const hw = COURT_WIDTH / 2 - 0.3   // inset slightly
    const hh = COURT_HEIGHT / 2 - 0.3
    const points = [
      new THREE.Vector3(-hw, 0, -hh),
      new THREE.Vector3( hw, 0, -hh),
      new THREE.Vector3( hw, 0,  hh),
      new THREE.Vector3(-hw, 0,  hh),
      new THREE.Vector3(-hw, 0, -hh),
    ]
    const lineGeo = new THREE.BufferGeometry().setFromPoints(points)
    this.scene.add(new THREE.Line(lineGeo, lineMat))
  }

  setEvening(on: boolean): void {
    if (on) {
      this.scene.background = new THREE.Color(0x1a0a30)  // dark purple night sky
      this.ambientLight.intensity = 0.25
      this.eveningSpot1.intensity = 2.5
      this.eveningSpot2.intensity = 2.5
    } else {
      this.scene.background = new THREE.Color(0x87ceeb)
      this.ambientLight.intensity = 1.0
      this.eveningSpot1.intensity = 0
      this.eveningSpot2.intensity = 0
    }
  }

  handleResize(): void {
    const w = window.innerWidth
    const h = window.innerHeight
    this.renderer.setSize(w, h)

    // Fit court width into viewport, maintain aspect
    const aspect = w / h
    const courtAspect = COURT_WIDTH / COURT_HEIGHT
    let viewW: number, viewH: number

    if (aspect > courtAspect) {
      // wider than court — fit height
      viewH = COURT_HEIGHT / 2 + 1.5
      viewW = viewH * aspect
    } else {
      // taller than court — fit width
      viewW = COURT_WIDTH / 2 + 1.5
      viewH = viewW / aspect
    }

    this.camera.left   = -viewW
    this.camera.right  =  viewW
    this.camera.top    =  viewH
    this.camera.bottom = -viewH
    this.camera.updateProjectionMatrix()
  }

  render(): void {
    this.renderer.render(this.scene, this.camera)
  }
}
